import { Injectable, computed, inject, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { Observable, timer, of } from 'rxjs';
import { switchMap, map, catchError, first } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { ShowResponse, PermissionsResponse } from '../Interfaces/api/roles.interface';
import { RolesService } from '../Services/roles.service';
import {
  Permission,
  PermissionConfig,
  PermissionDependencies,
} from '../Models/avyna/permission.model';
import { ListRolesFacade } from './list-roles.facade';
import { ResourceStateService } from '../Modules/Shared/Services/resource-state.service';

@Injectable()
export class RoleFormFacade {
  private readonly _resourceState = inject(ResourceStateService);

  private readonly _rolesService = inject(RolesService);
  private readonly _listRolesFacade = inject(ListRolesFacade);
  private readonly _roleId = signal<string | null>(null);

  private readonly _baseResource = httpResource<ShowResponse>(() => {
    const roleId = this._roleId();
    if (roleId) return this._rolesService.show(roleId);
    return undefined;
  });

  private readonly _permissionsResource = httpResource<PermissionsResponse>(() => {
    return this._rolesService.permissions();
  });

  // ACTIONS
  public setRoleId(roleId: string | null) {
    if (this._roleId() === roleId) {
      if (roleId) this._baseResource.reload();
    } else {
      this._roleId.set(roleId);
    }
  }

  public async deleteRole(): Promise<void> {
    const id = this._roleId();
    if (!id) return;
    await firstValueFrom(this._rolesService.delete(id));
    this._listRolesFacade.reload();
  }

  public async saveRole(payload: { name: string; permissionIds: number[] }): Promise<void> {
    const id = this._roleId();
    if (id) {
      await firstValueFrom(
        this._rolesService.update(id, { name: payload.name, permissions: payload.permissionIds }),
      );
    } else {
      await firstValueFrom(
        this._rolesService.create({ name: payload.name, permissions: payload.permissionIds }),
      );
    }
    this._listRolesFacade.reload();
  }

  public nameValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value || control.value.length < 3) {
        return of(null);
      }
      const existingName = this._resourceState.valueOf(this._baseResource)?.data?.name;
      if (existingName && control.value === existingName) {
        return of(null);
      }
      return timer(500).pipe(
        switchMap(() => {
          const id = this._roleId() ?? undefined;
          return this._rolesService.checkName(control.value, id);
        }),
        map((response) => (response.data?.is_unique ? null : { name_duplicated: true })),
        catchError(() => of({ name_duplicated: true }))
      );
    };
  }

  // PUBLIC READ MODELS
  readonly role = computed(() => {
    return this._resourceState.valueOf(this._baseResource)?.data ?? null;
  });

  readonly isLoading = computed(
    () => this._baseResource.isLoading() || this._permissionsResource.isLoading(),
  );

  readonly permissions = computed((): Permission[] => {
    const data = this._resourceState.valueOf(this._permissionsResource)?.data;
    return Array.isArray(data?.permissions) ? data.permissions : [];
  });

  readonly permissionDependencies = computed((): PermissionDependencies => {
    return this._resourceState.valueOf(this._permissionsResource)?.data?.dependencies ?? new PermissionDependencies();
  });

  readonly permissionConfig = computed((): PermissionConfig[] => {
    return this._resourceState.valueOf(this._permissionsResource)?.data?.config ?? [];
  });
}
